const LOCAL_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

interface DateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function partsAt(date: Date, timeZone: string): DateParts {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(date);
  const out: Record<string, number> = {};
  for (const part of parts) {
    if (part.type !== 'literal') out[part.type] = Number(part.value);
  }
  for (const key of ['year', 'month', 'day', 'hour', 'minute', 'second']) {
    if (!Number.isFinite(out[key])) throw new RangeError(`Missing ${key} for ${timeZone}`);
  }
  return out as unknown as DateParts;
}

/** Convert a wall-clock value in the expo's IANA timezone to the RFC3339 instant sent to Go. */
export function zonedLocalToISO(value: string, timeZone: string): string | null {
  const match = LOCAL_PATTERN.exec(value);
  if (!match) return null;
  const desired = {
    year: Number(match[1]), month: Number(match[2]), day: Number(match[3]),
    hour: Number(match[4]), minute: Number(match[5]),
  };
  const desiredUTC = Date.UTC(
    desired.year, desired.month - 1, desired.day, desired.hour, desired.minute, 0,
  );
  let instant = desiredUTC;
  try {
    for (let i = 0; i < 3; i += 1) {
      const shown = partsAt(new Date(instant), timeZone);
      const shownUTC = Date.UTC(shown.year, shown.month - 1, shown.day, shown.hour, shown.minute, 0);
      const correction = desiredUTC - shownUTC;
      instant += correction;
      if (correction === 0) break;
    }
    const final = partsAt(new Date(instant), timeZone);
    if (
      final.year !== desired.year || final.month !== desired.month || final.day !== desired.day
      || final.hour !== desired.hour || final.minute !== desired.minute
    ) return null;
    return new Date(instant).toISOString();
  } catch {
    return null;
  }
}

/** Render an RFC3339 instant as a datetime-local value in the expo's IANA timezone. */
export function isoToZonedLocal(value: string | undefined, timeZone: string): string {
  if (!value) return '';
  try {
    const p = partsAt(new Date(value), timeZone);
    const pad = (n: number): string => String(n).padStart(2, '0');
    return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}`;
  } catch {
    return '';
  }
}
